import { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, CssBaseline, ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';
import CheckoutPage from '../CheckoutPage';
import { fetchCheckoutPreview } from '../../services/checkoutConfig/checkoutConfig.service';
import { isCheckoutPreviewUpdateMessage, type CheckoutPreviewResponseDto } from '../../services/checkoutConfig/checkoutConfig.types';
import { mergePartialCheckoutConfig } from '../../services/checkoutConfig/checkoutConfig.merge';
import { buildFakePreviewOffer } from './fakeOffer';
import ErrorContext from '../../contexts/ErrorContext';
import { AnalyticsProvider } from '../../services/tracking';

/** No-op — a prévia não precisa exibir/descartar alertas de erro reais (produto fake, sem pagamento). */
const PREVIEW_ERROR_CONTEXT = { addError: () => {}, dismissError: () => {} };

interface PreviewCheckoutPageProps {
  checkoutNanoId: string;
}

/**
 * Modo preview: renderiza o checkout real (mesmos componentes de `CheckoutPage`) com um
 * produto fake e o `config` de um `store_checkouts` (rascunho ou publicado), atualizado
 * ao vivo via `postMessage` do iframe do admin — sem precisar de novo fetch a cada edição.
 *
 * Segurança: só aceita mensagens cuja `event.origin` bata com `VITE_ADMIN_ORIGIN`. Sem essa
 * env configurada, todas as mensagens são descartadas (fail-closed) — a prévia ainda funciona
 * com o fetch inicial, só não recebe atualizações ao vivo.
 */
export function PreviewCheckoutPage({ checkoutNanoId }: PreviewCheckoutPageProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<CheckoutPreviewResponseDto | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCheckoutPreview(checkoutNanoId)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Erro ao carregar prévia do checkout:', err);
        setError('Não foi possível carregar a prévia deste checkout.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [checkoutNanoId]);

  useEffect(() => {
    const expectedOrigin = import.meta.env.VITE_ADMIN_ORIGIN as string | undefined;

    const handleMessage = (event: MessageEvent) => {
      // Fail-closed: sem origin configurada, ou origin não bate, descarta silenciosamente.
      if (!expectedOrigin || event.origin !== expectedOrigin) return;
      if (!isCheckoutPreviewUpdateMessage(event.data)) return;

      setPreview((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          checkout: {
            ...prev.checkout,
            config: mergePartialCheckoutConfig(prev.checkout.config, event.data.config),
          },
        };
      });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const muiTheme = createTheme({
    palette: {
      primary: { main: preview?.checkout.config.general.buttonColor ?? '#3B82F6' },
    },
    typography: { fontFamily: 'Rubik, Poppins, sans-serif' },
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !preview) {
    return <Alert severity="error">{error ?? 'Prévia indisponível.'}</Alert>;
  }

  const fakeOffer = buildFakePreviewOffer(preview.checkout.name, checkoutNanoId, preview);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      <AnalyticsProvider>
        <ErrorContext.Provider value={PREVIEW_ERROR_CONTEXT}>
          <CheckoutPage
            shopifyOffer={fakeOffer}
            initialEditingSection="IDENTIFICATION"
            checkoutConfigOverride={preview.checkout.config}
            paymentBadgesOverride={preview.paymentBadges}
            storeContactOverride={preview.storeContact}
          />
        </ErrorContext.Provider>
      </AnalyticsProvider>
    </MuiThemeProvider>
  );
}
